import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import {
  createProfileQueryKey,
  createExperienceCollaboratorsQueryKey,
} from '@/utils/queryKeys';
import type {
  RemoveExperienceCollaboratorMutation,
  RemoveExperienceCollaboratorMutationVariables,
} from './useRemoveCollaborator.generated';

const query = gql`
  mutation RemoveExperienceCollaborator(
    $input: RemoveExperiencePermissionInput!
  ) {
    removeExperiencePermission(input: $input)
  }
`;

export const useRemoveCollaborator = () => {
  const queryClient = useQueryClient();
  const { experience } = usePageExperienceContext();
  const experienceId = String(experience?.id);

  const { mutate, isPending } = useMutation<
    RemoveExperienceCollaboratorMutation,
    QueryError,
    RemoveExperienceCollaboratorMutationVariables
  >({
    mutationFn: (variables) => {
      return gqlClient.request<
        RemoveExperienceCollaboratorMutation,
        RemoveExperienceCollaboratorMutationVariables
      >(query, variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createExperienceCollaboratorsQueryKey(experienceId),
      });
      queryClient.invalidateQueries({ queryKey: createProfileQueryKey() });
    },
  });

  const removeCollaborator = (
    userId: string,
    options?: MutateOptions<
      RemoveExperienceCollaboratorMutation,
      QueryError,
      RemoveExperienceCollaboratorMutationVariables
    >
  ) => mutate({ input: { experienceId, userId } }, options);

  return {
    removeCollaborator,
    isPending,
  };
};
